import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import { getPlanner, createPlanner, updatePlanner, getClients } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Combobox } from '../components/ui/combobox';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  ArrowLeft, Save, Loader2, Target, Users, Phone, Calendar,
  CircleDollarSign, BarChart3, Download, Trophy, UserCog,
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, Legend,
} from 'recharts';

const fmtEUR = (v) => new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);
const fmtNum = (v) => new Intl.NumberFormat('it-IT', { maximumFractionDigits: 1 }).format(v || 0);

// === DEFAULT DATA ===
// Periodicità di pianificazione: mensile / trimestrale / annuale (switch in UI)
// Modalità squadra: full_cycle | bdr_closer
const DEFAULT = {
  meta: {
    period: 'monthly',  // monthly | quarterly | annual
    mode: 'full_cycle',  // full_cycle | bdr_closer
    bdr_success_fee_enabled: false,  // se true, BDR riceve fee per appuntamento
  },
  objectives: {
    revenue_target: 0,        // fatturato target nel periodo
    avg_ticket: 0,            // ticket medio per vendita
    margin_pct: 0,            // marginalità %
    working_days: 22,         // giorni lavorativi/mese (riferimento)
  },
  funnel: {
    // Conversioni del processo (una sola tabella)
    outreach_per_day: 0,        // outreach giornaliero per persona
    response_rate: 0,           // % risposta su outreach
    appointment_rate: 0,        // % set appointment su risposta
    show_up_rate: 0,            // % show-up su appuntamenti fissati
    close_rate: 0,              // % chiusura su appuntamenti avvenuti
  },
  comp_full_cycle: {
    fixed_salary: 0,            // fisso mensile per venditore full-cycle
    commission_pct: 0,          // % provvigione sulla vendita
  },
  comp_split: {
    bdr_fixed_salary: 0,        // fisso mensile per BDR
    bdr_fee_per_appointment: 0, // fee per appuntamento avvenuto (se enabled)
    closer_fixed_salary: 0,     // fisso mensile per Closer
    closer_commission_pct: 0,   // % provvigione closer sulla vendita
  },
};

// === HELPERS ===
function NumInput({ label, value, onChange, suffix, prefix, hint }) {
  const [focused, setFocused] = useState(false);
  const [raw, setRaw] = useState('');
  const fmtDots = (n) => { if (!n && n !== 0) return ''; if (n === 0) return ''; return new Intl.NumberFormat('it-IT', { maximumFractionDigits: 2 }).format(n); };
  const display = focused ? raw : fmtDots(value);
  return (
    <div className="space-y-1">
      {label && <Label className="text-sm font-medium text-gray-700">{label}</Label>}
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{prefix}</span>}
        <Input
          type="text" inputMode="decimal" value={display}
          onChange={(e) => {
            const r = e.target.value; setRaw(r);
            const cleaned = r.replace(/\./g, '').replace(',', '.');
            if (cleaned === '' || cleaned === '-') return onChange(0);
            const n = parseFloat(cleaned); if (!isNaN(n)) onChange(n);
          }}
          onFocus={() => { setFocused(true); setRaw(value ? String(value) : ''); }}
          onBlur={() => setFocused(false)}
          className={`${prefix ? 'pl-8' : ''} ${suffix ? 'pr-10' : ''}`}
        />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm">{suffix}</span>}
      </div>
      {hint && <p className="text-xs text-gray-400">{hint}</p>}
    </div>
  );
}

function KPICard({ icon: Icon, title, value, subtitle, color = 'orange' }) {
  const colors = {
    orange: 'from-orange-50 to-orange-100 text-orange-700 border-orange-200',
    blue: 'from-blue-50 to-blue-100 text-blue-700 border-blue-200',
    emerald: 'from-emerald-50 to-emerald-100 text-emerald-700 border-emerald-200',
    purple: 'from-purple-50 to-purple-100 text-purple-700 border-purple-200',
    amber: 'from-amber-50 to-amber-100 text-amber-700 border-amber-200',
    red: 'from-red-50 to-red-100 text-red-700 border-red-200',
  };
  return (
    <Card className={`bg-gradient-to-br ${colors[color] || colors.orange} border`}>
      <CardContent className="p-3">
        <div className="flex items-center gap-2 mb-1">
          <Icon className="w-4 h-4" />
          <span className="text-xs font-medium uppercase tracking-wide">{title}</span>
        </div>
        <div className="text-xl font-bold">{value}</div>
        {subtitle && <div className="text-xs opacity-70 mt-0.5">{subtitle}</div>}
      </CardContent>
    </Card>
  );
}

// === CALCS ===
function computeMonthly(d) {
  const wd = Math.max(1, d.objectives.working_days || 22);
  const ticket = d.objectives.avg_ticket || 0;
  const target = d.objectives.revenue_target || 0;
  // n. vendite necessarie nel periodo
  const salesTarget = ticket > 0 ? target / ticket : 0;
  // funnel: working back from sales
  const close = (d.funnel.close_rate || 0) / 100;
  const show = (d.funnel.show_up_rate || 0) / 100;
  const appt = (d.funnel.appointment_rate || 0) / 100;
  const resp = (d.funnel.response_rate || 0) / 100;
  const apptCompleted = close > 0 ? salesTarget / close : 0;
  const apptScheduled = show > 0 ? apptCompleted / show : 0;
  const responses = appt > 0 ? apptScheduled / appt : 0;
  const outreach = resp > 0 ? responses / resp : 0;
  // per person capacity
  const outreachPerPersonMonth = (d.funnel.outreach_per_day || 0) * wd;
  // For full-cycle: 1 persona fa tutto
  // For split: BDR fa outreach (e fissa appuntamenti); Closer fa show-up e chiusura
  const isSplit = d.meta.mode === 'bdr_closer';
  // BDR capacity = outreach per month per person
  const bdrNeeded = isSplit && outreachPerPersonMonth > 0 ? outreach / outreachPerPersonMonth : 0;
  // Closer capacity: stimiamo che 1 closer faccia X appuntamenti show-up al mese.
  // Usiamo il dato bdr_fee_per_appointment come riferimento? No, è denaro. Usiamo working_days * 2 (default) come capacità appt avvenuti.
  // Per semplicità: closer fa 2 appt avvenuti/giorno = wd*2 appt/mese
  const closerApptCapacity = wd * 2;
  const closersNeeded = isSplit && closerApptCapacity > 0 ? apptCompleted / closerApptCapacity : 0;
  // Full-cycle persons needed = max(outreach capacity, appt capacity)
  const fullCycleByOutreach = outreachPerPersonMonth > 0 ? outreach / outreachPerPersonMonth : 0;
  const fullCycleByAppt = closerApptCapacity > 0 ? apptCompleted / closerApptCapacity : 0;
  const fullCycleNeeded = Math.max(fullCycleByOutreach, fullCycleByAppt);
  
  // Personal results (per persona)
  let personSales = 0, personRev = 0, personComm = 0, personFixed = 0;
  let teamFixed = 0, teamVariable = 0;
  if (isSplit) {
    const bdr = Math.max(1, bdrNeeded || 1);
    const closer = Math.max(1, closersNeeded || 1);
    teamFixed = bdr * (d.comp_split.bdr_fixed_salary || 0) + closer * (d.comp_split.closer_fixed_salary || 0);
    // BDR variable: fee per appointment (if enabled)
    const bdrVar = d.meta.bdr_success_fee_enabled ? (apptCompleted * (d.comp_split.bdr_fee_per_appointment || 0)) : 0;
    const closerVar = target * ((d.comp_split.closer_commission_pct || 0) / 100);
    teamVariable = bdrVar + closerVar;
    // Personal closer
    personSales = salesTarget / closer;
    personRev = target / closer;
    personComm = closerVar / closer;
    personFixed = d.comp_split.closer_fixed_salary || 0;
  } else {
    const persons = Math.max(1, fullCycleNeeded || 1);
    teamFixed = persons * (d.comp_full_cycle.fixed_salary || 0);
    teamVariable = target * ((d.comp_full_cycle.commission_pct || 0) / 100);
    personSales = salesTarget / persons;
    personRev = target / persons;
    personComm = teamVariable / persons;
    personFixed = d.comp_full_cycle.fixed_salary || 0;
  }
  
  const teamCost = teamFixed + teamVariable;
  const grossMargin = target * ((d.objectives.margin_pct || 0) / 100);
  const netMargin = grossMargin - teamCost;
  const ratio = teamCost > 0 ? target / teamCost : 0;
  
  return {
    isSplit,
    salesTarget, outreach, responses, apptScheduled, apptCompleted,
    outreachPerPersonMonth, closerApptCapacity,
    bdrNeeded, closersNeeded, fullCycleNeeded,
    personSales, personRev, personComm, personFixed,
    teamFixed, teamVariable, teamCost, grossMargin, netMargin, ratio,
  };
}

function scaleByPeriod(monthly, period) {
  const mult = period === 'annual' ? 12 : period === 'quarterly' ? 3 : 1;
  return {
    ...monthly,
    salesTarget: monthly.salesTarget * mult,
    outreach: monthly.outreach * mult,
    responses: monthly.responses * mult,
    apptScheduled: monthly.apptScheduled * mult,
    apptCompleted: monthly.apptCompleted * mult,
    personSales: monthly.personSales * mult,
    personRev: monthly.personRev * mult,
    personComm: monthly.personComm * mult,
    teamFixed: monthly.teamFixed * mult,
    teamVariable: monthly.teamVariable * mult,
    teamCost: monthly.teamCost * mult,
    grossMargin: monthly.grossMargin * mult,
    netMargin: monthly.netMargin * mult,
  };
}

export default function RecruitmentPlannerEditorPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { hasRole } = useAuth();
  const isReadOnly = !hasRole(['admin', 'project_manager']);
  const isNew = !id;
  
  const [name, setName] = useState('Nuovo Planner Reclutamento & Vendita');
  const [clientId, setClientId] = useState('');
  const [data, setData] = useState(() => JSON.parse(JSON.stringify(DEFAULT)));
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('obiettivi');
  
  // Period: il calcolo è mensile-base; viene scalato in vista. Mensile è anche il default.
  // Il valore di revenue_target rappresenta il fatturato del periodo selezionato.
  // Per evitare confusione, le formule lavorano sempre sui valori del periodo scelto (monthly).
  // Lo switch periodo è quindi solo "visivo" sulle proiezioni totali della squadra.
  
  useEffect(() => {
    (async () => {
      try {
        const res = await getClients();
        setClients(res.data.data || res.data);
      } catch { /* ignore */ }
    })();
  }, []);
  
  const autoCreated = useRef(false);
  useEffect(() => {
    if (isNew && !autoCreated.current) {
      autoCreated.current = true;
      (async () => {
        try {
          const res = await createPlanner({
            name: 'Nuovo Planner Reclutamento & Vendita',
            client_id: null,
            kind: 'recruitment_sales',
            data,
          });
          navigate(`/toolkit/recruitment-planner/${res.data.id}`, { replace: true });
        } catch {
          toast({ title: 'Errore', description: 'Impossibile creare il planner', variant: 'destructive' });
          setLoading(false);
        }
      })();
    } else if (!isNew) {
      (async () => {
        try {
          const res = await getPlanner(id);
          const p = res.data;
          setName(p.name || '');
          setClientId(p.client_id || '');
          if (p.data && Object.keys(p.data).length > 0) {
            // Merge with DEFAULT to preserve schema
            const merged = JSON.parse(JSON.stringify(DEFAULT));
            Object.keys(p.data).forEach(section => {
              if (merged[section] && typeof merged[section] === 'object') {
                merged[section] = { ...merged[section], ...p.data[section] };
              }
            });
            setData(merged);
          }
        } catch {
          toast({ title: 'Planner non trovato', variant: 'destructive' });
          navigate('/toolkit');
        } finally {
          setLoading(false);
        }
      })();
    }
  }, [id]);
  
  const updateField = useCallback((section, field, value) => {
    setData(prev => ({ ...prev, [section]: { ...prev[section], [field]: value } }));
  }, []);
  
  const handleSave = async () => {
    setSaving(true);
    try {
      await updatePlanner(id, { name, client_id: clientId || null, kind: 'recruitment_sales', data });
      toast({ title: 'Planner salvato' });
    } catch {
      toast({ title: 'Errore', description: 'Salvataggio fallito', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };
  
  // Auto-save with debounce
  const autoSaveTimer = useRef(null);
  useEffect(() => {
    if (isNew || loading || isReadOnly) return;
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(() => {
      updatePlanner(id, { name, client_id: clientId || null, kind: 'recruitment_sales', data }).catch(() => {});
    }, 1500);
    return () => { if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current); };
  }, [data, name, clientId, id, isNew, loading, isReadOnly]);
  
  const calc = useMemo(() => computeMonthly(data), [data]);
  const periodCalc = useMemo(() => scaleByPeriod(calc, data.meta.period), [calc, data.meta.period]);
  
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const M = 15;
      let y = 20;
      pdf.setFontSize(18);
      pdf.text(name || 'Planner Reclutamento & Vendita', M, y);
      y += 8;
      pdf.setFontSize(10);
      pdf.setTextColor(120);
      pdf.text(`Periodo: ${data.meta.period} · Modalità: ${data.meta.mode === 'full_cycle' ? 'Full-cycle' : 'BDR + Closer'}`, M, y);
      y += 10;
      pdf.setTextColor(0);
      pdf.setFontSize(13);
      pdf.text('KPI principali', M, y); y += 6;
      pdf.setFontSize(10);
      const kpis = [
        ['Fatturato target', fmtEUR(data.objectives.revenue_target)],
        ['Vendite necessarie', fmtNum(calc.salesTarget)],
        ['Outreach necessari', fmtNum(calc.outreach)],
        ['Appuntamenti fissati', fmtNum(calc.apptScheduled)],
        ['Appuntamenti avvenuti', fmtNum(calc.apptCompleted)],
        [`${calc.isSplit ? 'BDR necessari' : 'Persone necessarie'}`, calc.isSplit ? fmtNum(calc.bdrNeeded) : fmtNum(calc.fullCycleNeeded)],
        ...(calc.isSplit ? [['Closer necessari', fmtNum(calc.closersNeeded)]] : []),
        ['Costo squadra', fmtEUR(calc.teamCost)],
        ['Margine netto', fmtEUR(calc.netMargin)],
      ];
      kpis.forEach(([k, v]) => { pdf.text(`${k}:`, M, y); pdf.text(v, 100, y); y += 5; });
      pdf.save(`${name}.pdf`);
    } finally {
      setExporting(false);
    }
  };
  
  if (loading) {
    return <div className="p-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-orange-500" /></div>;
  }
  
  const isSplit = data.meta.mode === 'bdr_closer';
  const periodLabel = { monthly: 'mensile', quarterly: 'trimestrale', annual: 'annuale' }[data.meta.period];
  
  return (
    <div className="p-4 md:p-6 space-y-4 max-w-7xl mx-auto" data-testid="recruitment-planner-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate('/toolkit')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="text-xl font-semibold border-none shadow-none focus-visible:ring-0 px-0"
            disabled={isReadOnly}
            data-testid="planner-name-input"
          />
        </div>
        <div className="flex items-center gap-2">
          <Combobox
            value={clientId}
            onChange={setClientId}
            options={[{ value: '', label: 'Nessun cliente' }, ...clients.map(c => ({ value: c.id, label: c.company_name }))]}
            placeholder="Cliente"
            disabled={isReadOnly}
            className="w-56"
          />
          <Button variant="outline" onClick={handleExportPDF} disabled={exporting} data-testid="export-pdf-btn">
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            PDF
          </Button>
          {!isReadOnly && (
            <Button onClick={handleSave} disabled={saving} data-testid="save-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Salva
            </Button>
          )}
        </div>
      </div>
      
      {/* Mode + Period selector */}
      <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
        <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Modalità squadra:</span>
            <div className="flex bg-white rounded-md p-1 border border-gray-200">
              <button
                className={`px-3 py-1 text-sm rounded ${!isSplit ? 'bg-orange-500 text-white' : 'text-gray-600'}`}
                onClick={() => updateField('meta', 'mode', 'full_cycle')}
                data-testid="mode-full-cycle"
                disabled={isReadOnly}
              >Full-cycle</button>
              <button
                className={`px-3 py-1 text-sm rounded ${isSplit ? 'bg-orange-500 text-white' : 'text-gray-600'}`}
                onClick={() => updateField('meta', 'mode', 'bdr_closer')}
                data-testid="mode-bdr-closer"
                disabled={isReadOnly}
              >BDR + Closer</button>
            </div>
          </div>
          
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-700">Periodicità:</span>
            <div className="flex bg-white rounded-md p-1 border border-gray-200">
              {[
                { v: 'monthly', l: 'Mensile' },
                { v: 'quarterly', l: 'Trimestrale' },
                { v: 'annual', l: 'Annuale' },
              ].map(({ v, l }) => (
                <button
                  key={v}
                  className={`px-3 py-1 text-sm rounded ${data.meta.period === v ? 'bg-indigo-500 text-white' : 'text-gray-600'}`}
                  onClick={() => updateField('meta', 'period', v)}
                  data-testid={`period-${v}`}
                  disabled={isReadOnly}
                >{l}</button>
              ))}
            </div>
          </div>
        </CardContent>
      </Card>
      
      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4 lg:w-auto lg:inline-flex">
          <TabsTrigger value="obiettivi" data-testid="tab-obiettivi"><Target className="w-4 h-4 mr-1" />Obiettivi</TabsTrigger>
          <TabsTrigger value="conversioni" data-testid="tab-conversioni"><Phone className="w-4 h-4 mr-1" />Conversioni</TabsTrigger>
          <TabsTrigger value="provvigioni" data-testid="tab-provvigioni"><CircleDollarSign className="w-4 h-4 mr-1" />Provvigioni</TabsTrigger>
          <TabsTrigger value="output" data-testid="tab-output"><BarChart3 className="w-4 h-4 mr-1" />Output</TabsTrigger>
        </TabsList>
        
        {/* ============ OBIETTIVI ============ */}
        <TabsContent value="obiettivi">
          <Card>
            <CardHeader><CardTitle className="text-base">Obiettivi del periodo {periodLabel}</CardTitle></CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumInput
                label={`Fatturato target ${periodLabel}`}
                value={data.objectives.revenue_target}
                onChange={(v) => updateField('objectives', 'revenue_target', v)}
                prefix="€"
                hint={`Fatturato che vuoi generare nel periodo ${periodLabel}.`}
              />
              <NumInput
                label="Ticket medio"
                value={data.objectives.avg_ticket}
                onChange={(v) => updateField('objectives', 'avg_ticket', v)}
                prefix="€"
                hint="Importo medio di una vendita."
              />
              <NumInput
                label="Marginalità lorda"
                value={data.objectives.margin_pct}
                onChange={(v) => updateField('objectives', 'margin_pct', v)}
                suffix="%"
                hint="Margine lordo medio sulle vendite."
              />
              <NumInput
                label="Giorni lavorativi/mese"
                value={data.objectives.working_days}
                onChange={(v) => updateField('objectives', 'working_days', v)}
                suffix="gg"
                hint="Default 22 gg."
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ============ CONVERSIONI ============ */}
        <TabsContent value="conversioni">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Funnel di conversione del processo</CardTitle>
              <p className="text-xs text-gray-500 mt-1">Una sola tabella per l&apos;intero processo (si applica sia in modalità full-cycle che split).</p>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <NumInput
                label="Outreach al giorno per persona"
                value={data.funnel.outreach_per_day}
                onChange={(v) => updateField('funnel', 'outreach_per_day', v)}
                hint="Contatti generati al giorno da 1 venditore/BDR."
              />
              <NumInput
                label="% Risposta su outreach"
                value={data.funnel.response_rate}
                onChange={(v) => updateField('funnel', 'response_rate', v)}
                suffix="%"
              />
              <NumInput
                label="% Set appointment su risposta"
                value={data.funnel.appointment_rate}
                onChange={(v) => updateField('funnel', 'appointment_rate', v)}
                suffix="%"
              />
              <NumInput
                label="% Show-up appuntamenti"
                value={data.funnel.show_up_rate}
                onChange={(v) => updateField('funnel', 'show_up_rate', v)}
                suffix="%"
              />
              <NumInput
                label="% Chiusura su show-up"
                value={data.funnel.close_rate}
                onChange={(v) => updateField('funnel', 'close_rate', v)}
                suffix="%"
                hint="Vendite chiuse sugli appuntamenti effettivamente avvenuti."
              />
            </CardContent>
          </Card>
        </TabsContent>
        
        {/* ============ PROVVIGIONI ============ */}
        <TabsContent value="provvigioni">
          {!isSplit ? (
            <Card>
              <CardHeader><CardTitle className="text-base">Compensi Venditore Full-cycle</CardTitle></CardHeader>
              <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <NumInput label="Fisso mensile" value={data.comp_full_cycle.fixed_salary} onChange={(v) => updateField('comp_full_cycle', 'fixed_salary', v)} prefix="€" />
                <NumInput label="% Provvigione sulla vendita" value={data.comp_full_cycle.commission_pct} onChange={(v) => updateField('comp_full_cycle', 'commission_pct', v)} suffix="%" />
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Phone className="w-4 h-4 text-blue-500" />BDR</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <NumInput label="Fisso mensile BDR" value={data.comp_split.bdr_fixed_salary} onChange={(v) => updateField('comp_split', 'bdr_fixed_salary', v)} prefix="€" />
                  <div className="flex items-center justify-between p-3 rounded-md bg-blue-50 border border-blue-200">
                    <div>
                      <Label className="text-sm">Success Fee BDR (per appuntamento avvenuto)</Label>
                      <p className="text-xs text-gray-500">Attiva per dare un compenso variabile sul singolo appuntamento andato a buon fine.</p>
                    </div>
                    <Switch
                      checked={data.meta.bdr_success_fee_enabled}
                      onCheckedChange={(v) => updateField('meta', 'bdr_success_fee_enabled', v)}
                      disabled={isReadOnly}
                      data-testid="bdr-success-fee-switch"
                    />
                  </div>
                  {data.meta.bdr_success_fee_enabled && (
                    <NumInput
                      label="Fee per appuntamento avvenuto"
                      value={data.comp_split.bdr_fee_per_appointment}
                      onChange={(v) => updateField('comp_split', 'bdr_fee_per_appointment', v)}
                      prefix="€"
                    />
                  )}
                </CardContent>
              </Card>
              <Card>
                <CardHeader><CardTitle className="text-base flex items-center gap-2"><Trophy className="w-4 h-4 text-emerald-500" />Closer</CardTitle></CardHeader>
                <CardContent className="space-y-4">
                  <NumInput label="Fisso mensile Closer" value={data.comp_split.closer_fixed_salary} onChange={(v) => updateField('comp_split', 'closer_fixed_salary', v)} prefix="€" />
                  <NumInput label="% Provvigione Closer sulla vendita" value={data.comp_split.closer_commission_pct} onChange={(v) => updateField('comp_split', 'closer_commission_pct', v)} suffix="%" />
                </CardContent>
              </Card>
            </div>
          )}
        </TabsContent>
        
        {/* ============ OUTPUT ============ */}
        <TabsContent value="output">
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4">
            <KPICard icon={Target} title={`Fatturato ${periodLabel}`} value={fmtEUR(periodCalc.salesTarget * data.objectives.avg_ticket)} color="orange" />
            <KPICard icon={Trophy} title="Vendite" value={fmtNum(periodCalc.salesTarget)} color="emerald" />
            <KPICard icon={Phone} title="Outreach" value={fmtNum(periodCalc.outreach)} subtitle={`${fmtNum(periodCalc.outreach / Math.max(1, isSplit ? Math.ceil(calc.bdrNeeded) : Math.ceil(calc.fullCycleNeeded)))} per persona`} color="blue" />
            <KPICard icon={Calendar} title="Appuntamenti avvenuti" value={fmtNum(periodCalc.apptCompleted)} subtitle={`${fmtNum(periodCalc.apptScheduled)} fissati`} color="amber" />
            <KPICard
              icon={Users}
              title={isSplit ? 'BDR necessari' : 'Persone necessarie'}
              value={fmtNum(Math.ceil(isSplit ? calc.bdrNeeded : calc.fullCycleNeeded))}
              subtitle={isSplit ? `${fmtNum(Math.ceil(calc.closersNeeded))} closer` : null}
              color="purple"
            />
            <KPICard icon={CircleDollarSign} title="Margine netto" value={fmtEUR(periodCalc.netMargin)} color={periodCalc.netMargin >= 0 ? 'emerald' : 'red'} />
          </div>
          
          {/* Funnel breakdown table */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Breakdown Funnel ({periodLabel})</CardTitle></CardHeader>
              <CardContent>
                <div className="space-y-1 text-sm">
                  {[
                    { l: 'Outreach', v: fmtNum(periodCalc.outreach), hl: 'bg-blue-50 text-blue-800' },
                    { l: 'Risposte', v: fmtNum(periodCalc.responses) },
                    { l: 'Appuntamenti fissati', v: fmtNum(periodCalc.apptScheduled), hl: 'bg-amber-50 text-amber-800' },
                    { l: 'Appuntamenti avvenuti', v: fmtNum(periodCalc.apptCompleted), hl: 'bg-orange-50 text-orange-800' },
                    { l: 'Vendite', v: fmtNum(periodCalc.salesTarget), hl: 'bg-emerald-50 text-emerald-800' },
                    { l: 'Fatturato', v: fmtEUR(periodCalc.salesTarget * data.objectives.avg_ticket), hl: 'bg-purple-50 text-purple-800' },
                  ].map((r) => (
                    <div key={r.l} className={`flex justify-between items-center px-3 py-1.5 rounded-md ${r.hl ? r.hl + ' font-semibold border' : 'text-gray-600'}`}>
                      <span>{r.l}</span>
                      <span className={r.hl ? 'text-base font-bold' : 'font-semibold'}>{r.v}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
            
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-base">Costi e Risultato squadra ({periodLabel})</CardTitle></CardHeader>
              <CardContent className="space-y-1 text-sm">
                {isSplit ? (
                  <>
                    <Row label={`BDR necessari (${fmtNum(Math.ceil(calc.bdrNeeded))})`} value={fmtEUR(periodCalc.teamFixed * (calc.bdrNeeded / Math.max(0.001, calc.bdrNeeded + calc.closersNeeded)))} />
                    <Row label={`Closer necessari (${fmtNum(Math.ceil(calc.closersNeeded))})`} value={fmtEUR(periodCalc.teamFixed * (calc.closersNeeded / Math.max(0.001, calc.bdrNeeded + calc.closersNeeded)))} />
                  </>
                ) : (
                  <Row label={`Persone necessarie (${fmtNum(Math.ceil(calc.fullCycleNeeded))})`} value={fmtEUR(periodCalc.teamFixed)} />
                )}
                <Row label="Costo fisso squadra" value={fmtEUR(periodCalc.teamFixed)} bold />
                <Row label="Costo variabile squadra" value={fmtEUR(periodCalc.teamVariable)} bold />
                <Row label="Costo totale squadra" value={fmtEUR(periodCalc.teamCost)} bold highlight="bg-amber-50 text-amber-800 border" />
                <Row label="Fatturato" value={fmtEUR(periodCalc.salesTarget * data.objectives.avg_ticket)} bold />
                <Row label="Margine lordo" value={fmtEUR(periodCalc.grossMargin)} />
                <Row label="Margine netto" value={fmtEUR(periodCalc.netMargin)} bold highlight={periodCalc.netMargin >= 0 ? 'bg-emerald-50 text-emerald-800 border' : 'bg-red-50 text-red-800 border'} />
                <Row label="Ratio fatturato/costo squadra" value={`${calc.ratio.toFixed(2)}x`} />
              </CardContent>
            </Card>
          </div>
          
          {/* Personal targets */}
          <Card className="mt-4">
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <UserCog className="w-4 h-4 text-indigo-500" />
                Risultati attesi del singolo {isSplit ? 'closer' : 'venditore full-cycle'} ({periodLabel})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Stat label="Vendite" value={fmtNum(periodCalc.personSales)} />
                <Stat label="Fatturato personale" value={fmtEUR(periodCalc.personRev)} />
                <Stat label="Fisso mensile" value={fmtEUR(calc.personFixed)} />
                <Stat label={`Provvigioni ${periodLabel}`} value={fmtEUR(periodCalc.personComm)} />
                <Stat label={`Totale ${periodLabel}`} value={fmtEUR(periodCalc.personComm + calc.personFixed * (data.meta.period === 'annual' ? 12 : data.meta.period === 'quarterly' ? 3 : 1))} />
              </div>
            </CardContent>
          </Card>
          
          {/* Chart: funnel bars */}
          <Card className="mt-4">
            <CardHeader className="pb-2"><CardTitle className="text-base">Visualizzazione funnel</CardTitle></CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={260}>
                <BarChart
                  data={[
                    { name: 'Outreach', v: periodCalc.outreach },
                    { name: 'Risposte', v: periodCalc.responses },
                    { name: 'App. fissati', v: periodCalc.apptScheduled },
                    { name: 'App. avvenuti', v: periodCalc.apptCompleted },
                    { name: 'Vendite', v: periodCalc.salesTarget },
                  ]}
                  margin={{ top: 10, right: 20, bottom: 10, left: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="name" stroke="#6b7280" fontSize={11} />
                  <YAxis stroke="#6b7280" fontSize={11} />
                  <Tooltip formatter={(v) => fmtNum(v)} />
                  <Legend />
                  <Bar dataKey="v" name="Quantità" fill="#f97316" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Row({ label, value, bold, highlight }) {
  return (
    <div className={`flex justify-between items-center px-3 py-1.5 rounded-md ${highlight || ''}`}>
      <span className={bold ? 'font-medium text-gray-800' : 'text-gray-600'}>{label}</span>
      <span className={bold ? 'font-bold text-base' : 'font-semibold'}>{value}</span>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-md p-3 border border-gray-100">
      <div className="text-xs text-gray-500 uppercase tracking-wide">{label}</div>
      <div className="text-lg font-bold text-gray-900 mt-1">{value}</div>
    </div>
  );
}
